/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { wrapper } from "@context/store/store";
import {
  getPromotionByCode,
  getTenantAndStoresBySubdomain,
} from "@storeData/store";
import { toPathSlug } from "@util/utils";
import Head from "next/head";
import Link from "next/link";
import React, { Fragment } from "react";
import { useSelector } from "react-redux";
import Custom404 from "./404";

//

// Registrable suffixes made of two labels, so `brand.co.in` is treated as an apex domain.
const MULTI_LABEL_TLDS = new Set([
  "co.in",
  "net.in",
  "org.in",
  "co.uk",
  "co.nz",
  "co.za",
  "com.au",
  "com.br",
  "com.sg",
]);

/**
 * Platform hosts (`*.respark.in`) are unique by subdomain alone.
 * Custom hosts with a real subdomain (`booking.cadhairstudio.com`) can share that label across
 * brands, so the API also needs the main domain (`cadhairstudio.com`) to disambiguate.
 * Apex custom hosts (`empiresalon.in`, `www.empiresalon.in`) are looked up by brand label only —
 * sending `subdomain=www` returns an empty tenant and lands the user on /404.
 */
const resolveTenantLookupFromHost = (
  hostHeader?: string,
): {
  subdomain: string;
  domain?: string;
} => {
  const hostname = String(hostHeader || "")
    .split(":")[0]
    .toLowerCase();
  if (
    !hostname ||
    hostname === "localhost" ||
    hostname.endsWith(".localhost")
  ) {
    return { subdomain: "localhost" };
  }

  // `www` is never a tenant label.
  const parts = hostname.split(".").filter(Boolean);
  if (parts[0] === "www") parts.shift();

  if (parts.length < 2) {
    return { subdomain: parts[0] || hostname };
  }

  const isApex =
    parts.length === 2 ||
    (parts.length === 3 && MULTI_LABEL_TLDS.has(parts.slice(1).join(".")));
  if (isApex) {
    return { subdomain: parts[0] };
  }

  const domain = parts.slice(1).join(".");
  if (!domain || domain === "respark.in") {
    return { subdomain: parts[0] };
  }

  return { subdomain: parts[0], domain };
};

export const getServerSideProps = wrapper.getServerSideProps(
  async ({ req }) => {
    // habib.respark.in → subdomain only
    // bookings.cadhairstudio.com → subdomain + main domain (disambiguates shared labels like "booking")
    const { subdomain, domain } = resolveTenantLookupFromHost(req.headers.host);

    let promoCode = "";
    const tenantData: any = await getTenantAndStoresBySubdomain(
      subdomain,
      domain,
    );
    const metaTags = {
      title: tenantData.name || "",
      description: tenantData.description || "",
      image: tenantData.logo || "",
    };
    //if promotion presents
    if (req.url.includes("/?id")) {
      const promotionData: any = await getPromotionByCode(
        req.url.split("/?id=")[1],
      );
      promoCode = req.url.split("/?id=")[1];
      if (promotionData) {
        metaTags.title = promotionData.title || "";
        metaTags.description = promotionData.description || "";
        metaTags.image = promotionData.image || "";
      }
    }

    if (tenantData && tenantData?.stores && tenantData?.stores?.length != 0) {
      if (tenantData.stores?.length == 1) {
        const url = `/${toPathSlug(tenantData.name)}-${tenantData.id}/${toPathSlug(tenantData.stores[0].name)}${promoCode ? "?id=" + promoCode : ""}`;
        return {
          redirect: {
            permanent: false,
            destination: url,
          },
        };
      } else {
        return {
          props: { tenantData, metaTags },
        };
      }
    } else {
      return {
        redirect: {
          permanent: false,
          destination: "/404",
        },
      };
    }
  },
);

const TenantPage = ({ tenantData, metaTags }) => {
  if (tenantData && tenantData.id) {
    const titleTagData = metaTags.title || "";
    const descriptionTagData = metaTags.description || "";
    const imageTagData = metaTags.image || "";
    const store = useSelector((state: any) => state);
    const state = useSelector((state: any) => state);
    const languageData: any = state.languageKeywords;
    return (
      <React.Fragment>
        <Head>
          {/* Coomon meta tags */}
          <title>{titleTagData}</title>
          <meta name="title" key="title" content={titleTagData} />
          <meta
            name="description"
            key="description"
            content={descriptionTagData}
          />

          <meta
            property="og:site_name"
            content={titleTagData}
            key="ogsitename"
          />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={titleTagData} key="ogtitle" />
          <meta
            property="og:description"
            content={descriptionTagData}
            key="ogdesc"
          />
          <meta property="og:image" content={imageTagData} key="ogimage" />

          {/* Twitter meta Tags */}
          <meta
            name="twitter:title"
            content={titleTagData}
            key="twittertitle"
          />
          <meta
            name="twitter:description"
            content={descriptionTagData}
            key="twitterdesc"
          />
          <meta
            name="twitter:image"
            content={imageTagData}
            key="twitterimage"
          />
          <meta name="twitter:creator" content="Respark" />
          {/* application meta tags */}
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
          />
          <link rel="shortcut icon" type="image/png" href="/favicon.ico" />
        </Head>

        <div className="tenant-page-wrap stores-list-wrap d-f-c">
          <div className="tenant-details">
            <div className="heading d-f-c cap-text">
              {" "}
              {languageData["Welcome to"]
                ? languageData["Welcome to"]
                : "Welcome to"}
            </div>
            {Boolean(tenantData?.logoPath) ? (
              <div className="logo-wrap glass-card d-f-c">
                <img src={tenantData?.logoPath} />
              </div>
            ) : (
              <div className="store-name"> {tenantData.name}</div>
            )}
            <div className="sub-heading">
              {" "}
              {languageData["Please select your"]
                ? languageData["Please select your"]
                : "Please select your"}{" "}
              {Boolean(tenantData?.keywords?.branch)
                ? tenantData?.keywords?.branch
                : "store"}
            </div>
          </div>
          <div className="stores-list-wrap">
            {tenantData?.stores.map((store: any, i: number) => {
              return (
                <Fragment key={i}>
                  <Link
                    href={`/${toPathSlug(tenantData.name)}-${tenantData.id}/${toPathSlug(store.name)}`}
                  >
                    <div
                      style={{
                        color: `${tenantData.textColor}` || "#f7f2f2",
                        backgroundColor: `${tenantData.color}` || "#050505",
                      }}
                      className="store-details glass-card cap-text"
                      key={i}
                    >
                      {store.storeDisplayName || store.name}
                    </div>
                  </Link>
                </Fragment>
              );
            })}
          </div>
        </div>
      </React.Fragment>
    );
  } else return <Custom404 />;
};

export default TenantPage;
